import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Crown, Loader2, Send, Vote } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useCountdown } from "@/lib/gameUi";

function Timer({ state }) {
  const t = useCountdown(state.time_left, state.current?.time_limit);
  return <span data-testid="meme-phone-timer" className={`font-display font-black text-xl ${t <= 5 ? "text-rose-400" : "text-white"}`}>{Math.ceil(t)}s</span>;
}

function MiniMeme({ state }) {
  const m = state.current.meme;
  return <img data-testid="meme-phone-image" src={mediaUrl(m.image)} alt={m.name} className="w-full max-h-48 object-contain rounded-2xl bg-black/50 border-2 border-white/80" />;
}

function Waiting({ title, sub }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-3">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-20 h-20 rounded-full bg-[#00FF66]/20 flex items-center justify-center"><Check className="w-10 h-10 text-[#00FF66]" /></motion.div>
      <p className="font-display font-black text-2xl text-white">{title}</p>
      {sub && <p className="font-head bg-white text-[#0D0714] rounded-xl px-4 py-3 font-bold">{sub}</p>}
    </div>
  );
}

export function MemePickPhone({ state, code, session, remote }) {
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(null); }, [state.current?.id]);
  if (state.my_play) return <Waiting title="Card played!" sub={state.my_play} />;
  const hand = state.my_hand || [];
  const play = async () => {
    if (sel == null) return;
    setBusy(true);
    try { await api.post(`/rooms/${code}/meme/play`, { player_id: session.player_id, player_token: session.player_token, card: sel }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't play that card"); }
    setBusy(false);
  };
  return (
    <div data-testid="meme-pick-phone" className="flex-1 flex flex-col px-4 py-4 gap-3">
      <div className="flex items-center justify-between"><p className="font-head uppercase tracking-[0.25em] text-cyan-300 text-xs">Pick your funniest caption</p><Timer state={state} /></div>
      {remote && <MiniMeme state={state} />}
      <div className="flex flex-col gap-2.5">
        {hand.map((c, i) => (
          <motion.button key={`${c}-${i}`} data-testid={`meme-card-${i}`} onClick={() => setSel(i)} whileTap={{ scale: 0.97 }}
            initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1, scale: sel === i ? 1.02 : 1 }} transition={{ delay: i * 0.04 }}
            className={`text-left rounded-2xl px-4 py-3.5 font-head font-bold text-base leading-snug transition-colors border-4 ${sel === i ? "bg-white text-[#0D0714] border-[#FF2A85] shadow-[4px_4px_0_#FF2A85]" : "bg-white/90 text-[#0D0714] border-transparent"}`}>
            {c}
          </motion.button>
        ))}
      </div>
      <div className="sticky bottom-0 z-20 mt-auto -mx-4 px-4 pt-3 pb-3 bg-[#0D0714]/95 backdrop-blur-md border-t border-white/10">
        <button data-testid="meme-play-card-btn" disabled={sel == null || busy} onClick={play}
          className="w-full bg-gradient-to-r from-[#FF2A85] to-[#8B5CF6] text-white glow-magenta rounded-2xl py-4 font-display font-black text-lg uppercase flex items-center justify-center gap-2 disabled:from-[#3a2750] disabled:to-[#3a2750] disabled:text-white/50 disabled:shadow-none">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Play this card
        </button>
      </div>
    </div>
  );
}

export function MemeVotePhone({ state, code, session, remote }) {
  const [busy, setBusy] = useState(false);
  const entries = (state.meme?.entries || []).filter((e) => e.id !== state.my_entry_id);
  if (state.my_vote) return <Waiting title="Vote locked in!" sub={(state.meme?.entries || []).find((e) => e.id === state.my_vote)?.text} />;
  if (!entries.length) return <Waiting title="Sit tight" sub="No other captions to vote on." />;
  const vote = async (entry_id) => {
    setBusy(true);
    try { await api.post(`/rooms/${code}/meme/vote`, { player_id: session.player_id, player_token: session.player_token, entry_id }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't vote"); }
    setBusy(false);
  };
  return (
    <div data-testid="meme-vote-phone" className="flex-1 flex flex-col px-4 py-4 gap-3">
      <div className="flex items-center justify-between"><p className="font-head uppercase tracking-[0.25em] text-cyan-300 text-xs flex items-center gap-2"><Vote className="w-4 h-4" /> Vote for the funniest</p><Timer state={state} /></div>
      {remote && <MiniMeme state={state} />}
      {state.my_entry_id && <p className="text-white/50 text-sm font-head">Your own caption is hidden — no self-votes!</p>}
      {entries.map((e, i) => (
        <motion.button key={e.id} data-testid={`meme-vote-option-${i}`} disabled={busy} onClick={() => vote(e.id)} whileTap={{ scale: 0.97 }}
          initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: i * 0.05 }}
          className="text-left bg-white text-[#0D0714] rounded-2xl px-4 py-4 font-head font-bold text-base leading-snug shadow-[4px_4px_0_#00F0FF] disabled:opacity-50">
          {e.text}
        </motion.button>
      ))}
    </div>
  );
}

export function MemeRevealPhone({ state, me }) {
  const results = state.meme?.results || [];
  const mine = results.find((r) => r.owner === me.id);
  const winner = results.find((r) => r.winner);
  return (
    <div data-testid="meme-reveal-phone" className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
      {mine?.winner ? (
        <><motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring" }}><Crown className="w-20 h-20 text-[#FFD700]" fill="#FFD700" /></motion.div>
          <p data-testid="meme-reveal-headline" className="font-display font-black text-3xl text-white">You won the round!</p></>
      ) : (
        <p data-testid="meme-reveal-headline" className="font-display font-black text-3xl text-white">{winner ? `${winner.owner_name} wins!` : "No winner this time"}</p>
      )}
      {mine && <p className="font-head text-white/70">Your caption got <span className="text-[#FFD700] font-black">{mine.votes}</span> vote{mine.votes === 1 ? "" : "s"}</p>}
      {mine && mine.points > 0 && <p data-testid="meme-reveal-points" className="font-display font-black text-4xl text-[#00FF66]">+{mine.points}</p>}
      {winner && <p className="bg-white text-[#0D0714] rounded-2xl px-4 py-3 font-head font-bold">{winner.text}</p>}
    </div>
  );
}
